import { env } from '@/lib/env';

// Entrada con Effetá Apps.
//
// No es un `fetch`: es una navegación de página entera a
// `GET /auth/login?returnTo=<camino>` del API. El back manda a la persona a
// Effetá y, a la vuelta, la deja en `<front><returnTo>` (sin pase en la URL:
// el front lo pide con `POST /auth/refresh`) o en `/login?error=<código>`.

export const LOGIN_PATH = '/login';
export const LOGGED_OUT_PATH = '/logged-out';

// Quien llega con un enlace viejo del correo de contraseña
// (`/reset-password?token=…`) ve la entrada con este aviso. El router lo
// redirige sin el token, que así no queda en el historial.
export const PASSWORD_LINK_NOTICE = 'password-link';

// Reglas del back para `returnTo`. Si no las cumple, el back lo ignora y
// devuelve a `/`; acá lo descartamos antes para no armar URLs que el back va a
// tirar y para no volver a una pantalla de entrada (rebotaría sobre sí misma).
const RETURN_TO_MAX_LENGTH = 512;
const AUTH_SCREEN_PATHS: readonly string[] = [LOGIN_PATH, LOGGED_OUT_PATH];

function hasControlCharacters(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

// Devuelve el camino si sirve como `returnTo`, o `null`: tiene que empezar con
// `/` (no con `//`), no llevar `\` ni caracteres de control, medir hasta 512 y
// no apuntar a `/login` ni a `/logged-out`.
export function sanitizeReturnTo(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value.length > RETURN_TO_MAX_LENGTH) return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  if (value.includes('\\') || hasControlCharacters(value)) return null;
  const pathname = value.split(/[?#]/)[0] ?? '';
  if (AUTH_SCREEN_PATHS.includes(pathname)) return null;
  return value;
}

export function buildEffetaLoginUrl(returnTo: string): string {
  const base = env.VITE_API_BASE_URL.replace(/\/+$/, '');
  return `${base}/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

// Sin destino, la vuelta es a `/login`: con la sesión ya abierta, la pantalla
// de entrada lleva al destino por defecto (`@/lib/default-landing`), que no
// sobrevive al viaje de ida y vuelta por Effetá.
export function startEffetaLogin(returnTo: string | null): void {
  window.location.assign(buildEffetaLoginUrl(returnTo ?? LOGIN_PATH));
}

// Ruta del front a la pantalla de entrada, recordando a dónde se quería ir.
export function buildLoginRoute(returnTo: string): string {
  const safe = sanitizeReturnTo(returnTo);
  return safe === null ? LOGIN_PATH : `${LOGIN_PATH}?returnTo=${encodeURIComponent(safe)}`;
}
